import { Directive, EventEmitter, Inject, Injectable, OnDestroy, Output } from '@angular/core';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from "@azure/msal-angular";
import { AuthenticationResult, EventMessage, EventType, InteractionStatus, RedirectRequest } from '@azure/msal-browser';
import { Constants } from '@hub/common/Constants';
import { StorageService } from '@hub/services/StorageService';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

@Directive()
@Injectable({ providedIn: 'root' })
export class AuthService implements OnDestroy {
	constructor(
		@Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
		private storageService: StorageService,
		private msalService: MsalService,
		private msalBroadcastService: MsalBroadcastService
	) {
	}

	private readonly _destroying$ = new Subject<void>();
	private _redirectUrl: string = "";
	private interactionStatus: InteractionStatus = InteractionStatus.Startup;
	private loginRedirectStarted: boolean = false;

	// Event Emitters
	@Output()
	public getLoginStatus: EventEmitter<boolean> = new EventEmitter();

	// Properties
	get isLogged(): boolean {
		let activeAccount = this.msalService.instance.getActiveAccount();
		
		if (!!activeAccount)
			return true;
		else
			return this.msalService.instance.getAllAccounts().length > 0;
	}

	public get redirectUrl(): string {
		return this._redirectUrl;
	}
	public set redirectUrl(value: string) {
		this._redirectUrl = value;
	}

	public get UserAvatar(): string {
		return this.storageService.get(Constants.USER_AVATAR_KEY);
	}

	public get isInteractionInProgress(): boolean {
		return this.loginRedirectStarted || this.interactionStatus !== InteractionStatus.None;
	}

	public init() {
		this.msalBroadcastService.msalSubject$.pipe(
			filter((msg: EventMessage) =>
				msg.eventType === EventType.LOGIN_SUCCESS || msg.eventType === EventType.SSO_SILENT_SUCCESS
			),
			takeUntil(this._destroying$)
		).subscribe((result: EventMessage) => {
			const payload = result.payload as AuthenticationResult;
			this.msalService.instance.setActiveAccount(payload.account);
			},
			err => {
				console.error(err)
			}
		);

		this.msalBroadcastService.inProgress$.pipe(
			takeUntil(this._destroying$)
		).subscribe(
			(status: InteractionStatus) => {
				this.interactionStatus = status;

				if (status === InteractionStatus.None) {
					this.loginRedirectStarted = false;
					this.checkAndSetActiveAccount();
				}
			},
			err => {
				console.error(err)
			}
		);
	}

	checkAndSetActiveAccount() {
		let activeAccount = this.msalService.instance.getActiveAccount();

		if (!!activeAccount) {
			this.msalService.instance.setActiveAccount(activeAccount);
		}
		else {
			if (this.msalService.instance.getAllAccounts().length > 0) {
				let accounts = this.msalService.instance.getAllAccounts();
				this.msalService.instance.setActiveAccount(accounts[0]);
			}
		}
	}

	public login() {
		if (this.msalService.instance.getAllAccounts().length > 0 || this.isInteractionInProgress) {
			return;
		}

		this.loginRedirectStarted = true;

		if (this.msalGuardConfig.authRequest) {
			this.msalService.loginRedirect({ ...this.msalGuardConfig.authRequest } as RedirectRequest);
		} else {
			this.msalService.loginRedirect();
			this.getLoginStatus.emit(true);
		}
	}

	public logOut(): void {
		this.msalService.instance.setActiveAccount(null);
		this.msalService.logoutRedirect();
		this.getLoginStatus.emit(false);
	}

	ngOnDestroy(): void {
		this._destroying$.next(undefined);
		this._destroying$.complete();
	}

	public getUserEmail(): string | null {
		const account = this.msalService.instance.getActiveAccount() || this.msalService.instance.getAllAccounts()[0];
		return account ? account.username : null;
	}
}
